import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  useSyncExternalStore,
} from "react";
import { App, ConfigProvider } from "antd";
import {
  BRANDS,
  DEFAULT_BRAND,
  THEME_MODES,
  THEME_PREVIEW_ENABLED,
  THEME_STORAGE_KEY,
  THEME_STYLE_ELEMENT_ID,
  isDarkReadyRoute,
  resolveTheme,
} from "./themeConfig";
import { buildThemeStylesheet, getAntdTheme, getSemanticValues } from "./buildTheme";

const isBrowser = typeof window !== "undefined";
const DARK_QUERY = "(prefers-color-scheme: dark)";

const readPreference = () => {
  if (!isBrowser) return {};
  try {
    return JSON.parse(window.localStorage.getItem(THEME_STORAGE_KEY) || "{}") || {};
  } catch (e) {
    return {};
  }
};

const writePreference = preference => {
  try {
    window.localStorage.setItem(THEME_STORAGE_KEY, JSON.stringify(preference));
  } catch (e) {
    // Storage unavailable (private mode) — preference just won't survive a reload.
  }
};

const usePrefersDark = enabled => {
  const [prefersDark, setPrefersDark] = useState(
    () => isBrowser && Boolean(window.matchMedia?.(DARK_QUERY).matches)
  );
  useEffect(() => {
    if (!enabled || !window.matchMedia) return undefined;
    const media = window.matchMedia(DARK_QUERY);
    const onChange = e => setPrefersDark(e.matches);
    setPrefersDark(media.matches);
    media.addEventListener("change", onChange);
    return () => media.removeEventListener("change", onChange);
  }, [enabled]);
  return prefersDark;
};

// Static antd APIs (message.*, notification.*, Modal.confirm) render in their own
// React root, outside our ConfigProvider. `holderRender` wraps that root; this tiny
// store lets it re-render when the theme changes.
let staticTheme = getAntdTheme(THEME_MODES.LIGHT, DEFAULT_BRAND);
const staticListeners = new Set();
const staticThemeStore = {
  get: () => staticTheme,
  set: next => {
    staticTheme = next;
    staticListeners.forEach(listener => listener());
  },
  subscribe: listener => {
    staticListeners.add(listener);
    return () => staticListeners.delete(listener);
  },
};
const StaticHolderTheme = ({ children }) => {
  const current = useSyncExternalStore(
    staticThemeStore.subscribe,
    staticThemeStore.get,
    staticThemeStore.get
  );
  return <ConfigProvider theme={current}>{children}</ConfigProvider>;
};
ConfigProvider.config({
  holderRender: children => <StaticHolderTheme>{children}</StaticHolderTheme>,
});

// gatsby-ssr inlines the variables for first paint. Re-sync them from the client
// bundle on mount: injects them if SSR didn't run, and replaces a stale copy (in
// `gatsby develop` the SSR'd <style> lags theme edits until a restart). In a
// production build the two are identical, so this is a no-op.
const ensureStylesheet = () => {
  if (!isBrowser) return;
  const css = buildThemeStylesheet();
  let style = document.getElementById(THEME_STYLE_ELEMENT_ID);
  if (!style) {
    style = document.createElement("style");
    style.id = THEME_STYLE_ELEMENT_ID;
    document.head.appendChild(style);
  }
  if (style.textContent !== css) style.textContent = css;
};

// "bg-container" → "bgContainer", so components write tokens.bgContainer.
const toCamelKeys = values =>
  Object.fromEntries(
    Object.entries(values).map(([name, value]) => [
      name.replace(/-([a-z])/g, (_, c) => c.toUpperCase()),
      value,
    ])
  );

const ThemeContext = createContext(null);

/**
 * Root theme provider: owns the mode/brand preference, resolves what actually
 * renders (preview gate + dark-ready routes), keeps <html data-theme/data-brand>
 * in sync, and feeds antd's ConfigProvider from the same config as the CSS vars.
 */
export const ThemeProvider = ({ pathname = "", children }) => {
  const [preference, setPreference] = useState(readPreference);
  const prefersDark = usePrefersDark(
    THEME_PREVIEW_ENABLED && preference.mode === THEME_MODES.SYSTEM
  );
  const { mode, brand } = resolveTheme({ preference, pathname, prefersDark });
  const antdThemeConfig = useMemo(() => getAntdTheme(mode, brand), [mode, brand]);
  const tokens = useMemo(() => toCamelKeys(getSemanticValues(mode, brand)), [mode, brand]);

  useEffect(ensureStylesheet, []);

  useEffect(() => {
    const root = document.documentElement;
    root.setAttribute("data-theme", mode);
    root.setAttribute("data-brand", brand);
    staticThemeStore.set(antdThemeConfig);
  }, [mode, brand, antdThemeConfig]);

  const updatePreference = useCallback(patch => {
    setPreference(prev => {
      const next = { ...prev, ...patch };
      writePreference(next);
      return next;
    });
  }, []);

  const value = useMemo(
    () => ({
      mode, // what renders: "light" | "dark"
      isDark: mode === THEME_MODES.DARK,
      brand, // what renders: a BRANDS key
      tokens, // resolved colours: { primary, accent, accentBg, bgContainer, textSecondary, … }
      preferredMode: preference.mode || THEME_MODES.LIGHT, // what the user picked (may be "system")
      setMode: next => updatePreference({ mode: next }),
      setBrand: next => BRANDS[next] && updatePreference({ brand: next }),
      isPreviewEnabled: THEME_PREVIEW_ENABLED,
      isDarkAvailable: isDarkReadyRoute(pathname),
    }),
    [mode, brand, tokens, preference.mode, pathname, updatePreference]
  );

  return (
    <ThemeContext.Provider value={value}>
      <ConfigProvider theme={antdThemeConfig}>
        {/* component={false}: provides App.useApp() context without an extra DOM node. */}
        <App component={false}>{children}</App>
      </ConfigProvider>
    </ThemeContext.Provider>
  );
};

/**
 * Everything a component needs from the theme:
 *   const { isDark, mode, brand, tokens } = useTheme();
 *   tokens.primary, tokens.accent, tokens.bgContainer, tokens.textSecondary, …
 * Prefer Tailwind classes (bg-primary, text-secondary) — reach for `tokens` only
 * where a class can't go (chart configs, canvas, computed inline styles).
 */
export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) throw new Error("useTheme must be used inside <ThemeProvider>");
  return context;
};

/** Shorthand for `useTheme().tokens`. */
export const useThemeTokens = () => useTheme().tokens;
